import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Pagination, Row, Col } from "react-bootstrap";
import { BsFiletypeXls } from "react-icons/bs";
import { MdFilterListAlt, MdFilterAltOff, MdFilterAlt } from "react-icons/md";
import { RiFilterFill } from "react-icons/ri";
import Swal from "sweetalert2";
const API_URL = process.env.REACT_APP_API_URL;

function WalletReport() {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState({ amount: "", type: "" });
  const [showFilter, setShowFilter] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1199);
  const [totals, setTotals] = useState({ total_credit: 0, total_debit: 0 });
  const token = localStorage.getItem("token");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterToType, setFilterToType] = useState("");

  const pageLimit = 100;
  const toggleFilter = () => setShowFilter((prev) => !prev);
  const showCustomMessageModal = (title, text) => {
    alert(`${title}: ${text}`);
  };
  const [isFilterActive, setIsFilterActive] = useState(false);
  const handleToggle = () => {
    setIsFilterActive(!isFilterActive);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1199) {
        setIsDesktop(true);
        setShowFilter(true);
      } else {
        setIsDesktop(false);
        setShowFilter(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLedger = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm.type || filterToType) {
        params.append("type", searchTerm.type || filterToType);
      }
      if (filterFromDate) {
        params.append("from_date", formatDateForServer(filterFromDate));
      }
      if (filterToDate) {
        params.append("to_date", formatDateForServer(filterToDate));
      }
      params.append("page", page);
      params.append("limit", pageLimit);
      const url = `${API_URL}/ledger/list?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.success) {
        setLedger(result.data || result.ledger || []);
        setTotals(result.totals || { total_credit: 0, total_debit: 0 });
        const pagination = result.pagination || result.meta || {};
        setTotalPages(pagination.totalPages || pagination.last_page || 1);
        setCurrentPage(pagination.page || pagination.current_page || page);
      } else {
        showCustomMessageModal(
          "Error",
          result.message || "Failed to fetch records."
        );
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showCustomMessageModal(
        "Error",
        "Failed to fetch ledger data. Please check console for details."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLedger(1);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchLedger(pageNumber);
    }
  };

  const getPaginationGroup = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "-";
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateForServer = (dateStr) => {
    if (!dateStr) return "";

    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    if (parts[0].length === 2 && parts[2].length === 4) {
      const [dd, mm, yyyy] = parts;
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    } else if (parts[0].length === 4 && parts[2].length === 2) {
      return dateStr;
    }
    return "";
  };

  const handleDownloadFilteredExcel = async () => {
    const token = localStorage.getItem("token");

    try {
      Swal.fire({
        title: "Please wait...",
        text: "Generating your filtered Excel file",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        totalPages,
        amount: searchTerm.amount,
        type: searchTerm.type || filterToType,
        from_date: formatDateForServer(filterFromDate),
        to_date: formatDateForServer(filterToDate),
      }).toString();

      const response = await fetch(
        `${API_URL}/download-wallet-ledger-excel?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download Excel");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Filtered Users Wallet Ledger Report.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: "success",
        title: "Download Successful!",
        text: "Filtered Excel report has been downloaded successfully.",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      console.error("Filtered Excel download failed:", error);
      Swal.fire({
        icon: "warning",
        title: "Download Failed!",
        text: "Please Check Entered From Date & To Date For Downloding Users Wallet Ledger Report.",
      });
    }
  };

  const handleClearFilters = () => {
    setFilterFromDate("");
    setFilterToDate("");
    setFilterToType("");
    setSearchTerm({ amount: "", type: "" });
    setCurrentPage(1);
    fetchLedger(1);
  };

  // ═══════════ INLINE STYLES ═══════════
  const isMobile = window.innerWidth <= 768;

  const styles = {
    page: {
      padding: "16px",
      paddingTop: isMobile ? "0" : "16px",
      background: "#f1f5f9",
      minHeight: "calc(100vh - 110px)",
      fontFamily: '"Poppins", "Inter", sans-serif',
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    // ═══ PURPLE GRADIENT HEADER ═══
    headerCard: {
      background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)",
      borderRadius: "16px",
      padding: "18px 20px",
      color: "#ffffff",
      boxShadow: "0 10px 30px rgba(30, 27, 75, 0.35)",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    title: {
      fontSize: "18px",
      fontWeight: 700,
      margin: 0,
      color: "#ffffff",
      letterSpacing: "-0.01em",
    },
    subtitle: {
      fontSize: "12.5px",
      color: "rgba(255,255,255,0.85)",
      margin: 0,
    },
    actions: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    },
    btnExcel: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "9px 16px",
      borderRadius: "10px",
      border: "none",
      background: "#ffffff",
      color: "#1e1b4b",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      transition: "all 0.25s ease",
      whiteSpace: "nowrap",
    },
    btnFilter: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      borderRadius: "10px",
      border: isFilterActive ? "1px solid #ffffff" : "1px solid rgba(255,255,255,0.25)",
      background: isFilterActive ? "#ffffff" : "rgba(255,255,255,0.15)",
      color: isFilterActive ? "#1e1b4b" : "#ffffff",
      cursor: "pointer",
      transition: "all 0.25s ease",
      flexShrink: 0,
      fontSize: "18px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    filterSection: {
      marginTop: "18px",
      paddingTop: "18px",
      borderTop: "1px solid rgba(255,255,255,0.15)",
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1.3fr 1.5fr auto",
      gap: "14px",
      alignItems: "end",
    },
    filterItem: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      minWidth: 0,
    },
    filterLabel: {
      fontSize: "10.5px",
      fontWeight: 700,
      color: "rgba(255,255,255,0.9)",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      marginBottom: 0,
    },
    filterInput: {
      padding: "11px 14px",
      borderRadius: "10px",
      border: "1.5px solid rgba(255,255,255,0.25)",
      background: "rgba(255,255,255,0.12)",
      color: "#ffffff",
      fontSize: "13.5px",
      fontFamily: "inherit",
      outline: "none",
      width: "100%",
      boxSizing: "border-box",
      height: "44px",
      transition: "all 0.25s ease",
      fontWeight: 500,
    },
    filterBtnWrap: {
      display: "flex",
      gap: "8px",
      alignItems: "flex-end",
    },
    filterBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "0 22px",
      height: "44px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #e63946, #c1121f)",
      color: "#fff",
      fontSize: "13px",
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: "0 4px 14px rgba(230,57,70,0.4)",
      whiteSpace: "nowrap",
      transition: "all 0.25s ease",
      letterSpacing: "0.02em",
    },
    filterClearBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "0 22px",
      height: "44px",
      borderRadius: "10px",
      border: "1.5px solid rgba(255,255,255,0.3)",
      background: "rgba(255,255,255,0.1)",
      color: "#ffffff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.25s ease",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
      gap: "12px",
    },
    summaryCard: (color) => ({
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "16px",
      background: "#ffffff",
      borderRadius: "14px",
      boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
      borderLeft: `4px solid ${color}`,
      transition: "all 0.25s ease",
    }),
    summaryIcon: (bg) => ({
      width: "46px",
      height: "46px",
      borderRadius: "12px",
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "22px",
      flexShrink: 0,
    }),
    summaryLabel: {
      display: "block",
      fontSize: "11px",
      fontWeight: 600,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: "4px",
    },
    summaryValue: (color) => ({
      fontSize: "18px",
      fontWeight: 700,
      margin: 0,
      color: color,
      letterSpacing: "-0.01em",
    }),
    tableCard: {
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    tableWrap: {
      width: "100%",
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "700px",
      marginBottom: 0,
    },
    // ═══ PURPLE GRADIENT TABLE HEADER ═══
    thead: {
      background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)",
    },
    th: {
      padding: "12px 14px",
      fontSize: "11.5px",
      fontWeight: 700,
      color: "#ffffff",
      textAlign: "left",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      whiteSpace: "nowrap",
      border: "none",
    },
    td: {
      padding: "12px 14px",
      fontSize: "13px",
      color: "#1a1a2e",
      fontWeight: 500,
      verticalAlign: "middle",
      borderBottom: "1px solid #f1f5f9",
    },
    tdCredit: {
      color: "#10b981",
      fontWeight: 700,
    },
    tdDebit: {
      color: "#e63946",
      fontWeight: 700,
    },
    tdBalance: {
      color: "#0f172a",
      fontWeight: 700,
    },
    tdNarration: {
      maxWidth: "300px",
      wordBreak: "break-word",
      whiteSpace: "normal",
      color: "#475569",
      fontSize: "12.5px",
    },
    loading: {
      padding: "60px 20px",
      textAlign: "center",
      color: "#64748b",
      fontSize: "14px",
      fontWeight: 500,
    },
    paginationWrap: {
      display: "flex",
      flexDirection: "column",
      alignItems: isMobile ? "center" : "flex-end",
      gap: "10px",
      padding: "16px",
      borderTop: "1px solid #f1f5f9",
    },
    pagination: {
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: 0,
      justifyContent: "center",
    },
    pageBtn: (active) => ({
      minWidth: "36px",
      height: "36px",
      padding: "0 10px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: active ? "#4c1d95" : "#ffffff",
      color: active ? "#ffffff" : "#475569",
      fontSize: "12.5px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontFamily: "inherit",
    }),
    paginationInfo: {
      fontSize: "12px",
      color: "#64748b",
      fontWeight: 500,
      textAlign: isMobile ? "center" : "right",
      margin: 0,
    },
  };

  return (
    <div style={styles.page}>
      {/* ═══════════ HEADER CARD ═══════════ */}
      <div style={styles.headerCard}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.title}>Wallet Ledger Report</h2>
            <p style={styles.subtitle}>View all your wallet transactions</p>
          </div>
          <div style={styles.actions}>
            <button
              style={styles.btnExcel}
              onClick={handleDownloadFilteredExcel}
            >
              <BsFiletypeXls style={{ fontSize: "16px" }} />
              <span>Download</span>
            </button>
            <button
              style={styles.btnFilter}
              onClick={handleToggle}
            >
              {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
            </button>
          </div>
        </div>

        {/* ═══════════ FILTER SECTION ═══════════ */}
        {isFilterActive && (
          <div style={styles.filterSection}>
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>From Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
              />
            </div>
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>To Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
              />
            </div>
            <div style={styles.filterItem}>
              <label style={styles.filterLabel}>Transaction Type</label>
              <input
                type="text"
                placeholder="CR / DR"
                style={styles.filterInput}
                value={filterToType}
                onChange={(e) => setFilterToType(e.target.value)}
                className="filter-search-input"
              />
            </div>
            <div style={styles.filterBtnWrap}>
              <button
                style={styles.filterBtn}
                onClick={() => fetchLedger(1)}
              >
                <RiFilterFill /> Filter
              </button>
              <button
                style={styles.filterClearBtn}
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════ SUMMARY CARDS ═══════════ */}
      {!loading && ledger.length > 0 && (
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard("#10b981")}>
            <div style={styles.summaryIcon("#f0fdf4")}>💰</div>
            <div>
              <span style={styles.summaryLabel}>Total Credit</span>
              <h3 style={styles.summaryValue("#10b981")}>
                ₹ {formatAmount(totals.total_credit)}
              </h3>
            </div>
          </div>
          <div style={styles.summaryCard("#e63946")}>
            <div style={styles.summaryIcon("#fef2f2")}>💸</div>
            <div>
              <span style={styles.summaryLabel}>Total Debit</span>
              <h3 style={styles.summaryValue("#e63946")}>
                ₹ {formatAmount(totals.total_debit)}
              </h3>
            </div>
          </div>
          <div style={styles.summaryCard("#f59e0b")}>
            <div style={styles.summaryIcon("#fff7ed")}>💼</div>
            <div>
              <span style={styles.summaryLabel}>Current Balance</span>
              <h3 style={styles.summaryValue("#f59e0b")}>
                ₹ {formatAmount(totals.total_credit - totals.total_debit)}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ TABLE CARD ═══════════ */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.loading}>Loading records...</div>
        ) : ledger.length === 0 ? (
          <div style={styles.loading}>No records found.</div>
        ) : (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Narration</th>
                    <th style={styles.th}>Credit (Cr)</th>
                    <th style={styles.th}>Debit (Dr)</th>
                    <th style={styles.th}>Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((item, index) => {
                    const isCr =
                      item.transaction_type?.toUpperCase() === "CR";
                    const isDr =
                      item.transaction_type?.toUpperCase() === "DR";

                    return (
                      <tr key={item.id || index}>
                        <td style={styles.td}>
                          {(currentPage - 1) * pageLimit + index + 1}
                        </td>
                        <td style={styles.td}>{formatDate(item.date)}</td>
                        <td style={{ ...styles.td, ...styles.tdNarration }}>
                          {item.narration || "-"}
                        </td>
                        <td style={{ ...styles.td, ...styles.tdCredit }}>
                          {isCr ? `₹ ${formatAmount(item.amount)}` : "-"}
                        </td>
                        <td style={{ ...styles.td, ...styles.tdDebit }}>
                          {isDr ? `₹ ${formatAmount(item.amount)}` : "-"}
                        </td>
                        <td style={{ ...styles.td, ...styles.tdBalance }}>
                          ₹ {formatAmount(item.closing_balance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ═══════════ PAGINATION ═══════════ */}
            {totalPages > 1 && (
              <div style={styles.paginationWrap}>
                <div style={styles.pagination}>
                  <button
                    style={styles.pageBtn(false)}
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    « First
                  </button>
                  <button
                    style={styles.pageBtn(false)}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>

                  {getPaginationGroup().map((pageNumber, index) =>
                    pageNumber === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        style={{
                          padding: "0 8px",
                          color: "#94a3b8",
                          fontWeight: 600,
                        }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNumber}
                        style={styles.pageBtn(pageNumber === currentPage)}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    )
                  )}

                  <button
                    style={styles.pageBtn(false)}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    style={styles.pageBtn(false)}
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last »
                  </button>
                </div>

                <p style={styles.paginationInfo}>
                  Page {currentPage} of {totalPages} | Showing{" "}
                  {ledger.length} records (Serial:{" "}
                  {(currentPage - 1) * pageLimit + 1} to{" "}
                  {(currentPage - 1) * pageLimit + ledger.length})
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════ GLOBAL PLACEHOLDER STYLES ═══════════ */}
      <style>{`
        .filter-search-input::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
          font-weight: 400;
        }
      `}</style>
    </div>
  );
}

export default WalletReport;